import { useSyncExternalStore } from 'react';
import { useFormCoordinatorContext } from './FormCoordinatorContext.tsx';
import { type FormHandlerState } from './FormCoordinatorTypes.tsx';

// Fallback stabili (referenzialmente identici) per useSyncExternalStore
// quando il componente è renderizzato fuori da un FormCoordinator.
const noopSubscribe = () => () => {};
const EMPTY_FORMS: Record<string, FormHandlerState> = {};
const emptySnapshot = () => EMPTY_FORMS;

/**
 * Stato booleano aggregato (`isDirty`, `isValid`) di tutti i form coordinati,
 * senza causare `propagateContextChange_eager` sull'intero albero.
 *
 * Lo stato è mantenuto in un ref nel `useFormCoordinator`, non nello state
 * del context. Questo hook usa `useSyncExternalStore` per sottoscriversi ai
 * cambiamenti reali: lo snapshot cambia referenza solo quando un booleano
 * effettivamente flippa, quindi i re-render sono rari.
 *
 * Usare questo hook quando serve sapere **se** un form è dirty/invalido,
 * non **dove** (quale campo specifico). Consumer tipici: badge tab,
 * prevent navigation.
 *
 * Per stato granulare per-campo (`dirtyFields`, `errors`) usare
 * `useCoordinatedFormState`, che sottoscrive direttamente il control RHF
 * del form target e supporta il filtraggio per `name`.
 */
export function useCoordinatorForms(): Record<string, FormHandlerState> {
  const coordinator = useFormCoordinatorContext();

  return useSyncExternalStore(
    coordinator.subscribeToForms ?? noopSubscribe,
    coordinator.getFormsSnapshot ?? emptySnapshot,
  );
}
